<template>
  <div class="ms-power-bi-form">
    <m-content
      v-if="!getDatasetsInGroupLoading && hasError"
      :padding-top="0"
      :padding-x="11"
      :padding-bottom="2"
    >
      <data-source-query-error
        :data-source="dataSource"
        :error="error"
      />
    </m-content>
    <template v-if="!hasConnectionError">
      <m-content
        :padding-top="0"
        :padding-x="11"
        :padding-bottom="2"
      >
        <div class="_item">
          <div class="_label">
            {{ $t('msPowerBIForm.selectGroupLabel') }}
          </div>
          <m-select
            v-model:value="localQuery.groupId"
            :items="groups"
            value-key="id"
            item-text="name"
            full-width
            match-trigger-width
            show-search
            @change="changeGroup"
          />
        </div>
        <template
          v-if="localQuery.groupId !== null && dataSource[customDatasourceProperty.msPowerBiGroups].length > 0"
        >
          <div class="_item">
            <div class="_label">
              {{ $t('msPowerBIForm.selectDatasetLabel') }}
            </div>
            <m-select
              v-model:value="localQuery.datasetId"
              :items="dataSource[customDatasourceProperty.msPowerBiDatasets]"
              :loading="getDatasetsInGroupLoading"
              value-key="id"
              item-text="name"
              full-width
              match-trigger-width
              show-search
              @change="changeDataset"
            />
          </div>
        </template>
        <template
          v-if="localQuery.datasetId !== null && dataSource[customDatasourceProperty.msPowerBiDatasets].length > 0"
        >
          <div class="_item">
            <div class="_label">
              {{ $t('msPowerBIForm.daxQueryLabel') }}
            </div>
            <m-textarea
              v-model:value="localQuery.dax"
              :rows="5"
              full-width
              :placeholder="$t('msPowerBIForm.daxQueryPlaceholder')"
            />
          </div>
        </template>
        <div
          v-if="localQuery.groupId !== null && localQuery.datasetId !== null"
          class="_item"
          :style="{ marginTop: '2rem' }"
        >
          <always-sync-checkbox v-model:value="localQuery.alwaysSync" />
        </div>
      </m-content>
      <m-divider xs />
      <template v-if="localQuery.uid === undefined">
        <m-content padding-small>
          <div class="_actions">
            <div class="_btns">
              <m-btn
                class="_btn"
                @click="$emit('close')"
              >
                {{ $t('general.cancel') }}
              </m-btn>
              <m-btn
                color="primary"
                class="_btn"
                :loading="mutateLoading"
                :disabled="!isValid"
                @click="save"
              >
                {{ $t('general.save') }}
              </m-btn>
            </div>
          </div>
        </m-content>
      </template>
      <template v-else>
        <m-card-item
          icon="save"
          :loading="mutateLoading"
          :padding-x="8"
          :disabled="!isValid"
          @click="save"
        >
          {{ $t('general.save') }}
        </m-card-item>
        <m-card-item
          icon="sync"
          :loading="syncLoading"
          :padding-x="8"
          :disabled="!isValid || hasError"
          @click="syncNow"
        >
          {{ $t('dataSource.syncNow') }}
        </m-card-item>
        <m-card-item
          icon="delete"
          :padding-x="8"
          :loading="deleteLoading"
          @click="deleteMsPowerBIQuery"
        >
          {{ $t('general.delete') }}
        </m-card-item>
      </template>
    </template>
  </div>
</template>

<script setup>
import AlwaysSyncCheckbox from '@/components/datasource/AlwaysSyncCheckbox.vue';
import DataSourceQueryError from '@/components/datasource/DataSourceQueryError.vue';
import useConfirmDialog from '@/composables/confirm-dialog';
import useMSPowerBI from '@/composables/integrations/ms-powerbi/ms-powerbi';
import useSnackBar from '@/composables/snackbar';
import { computed, ref, watch } from 'vue';
import { copy } from 'shared/lib/copy';
import { customDatasourceProperty, dataSourceQueryGenericError } from 'shared/constants.json';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { logCatch } from '@/lib/logger/logger';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const snackbar = useSnackBar();

const props = defineProps({
  dataSource: {
    type: Object,
    required: true,
  },
  query: {
    type: Object,
    default: () => null,
  },
  goal: {
    type: Object,
    required: true,
  },
});
const emit = defineEmits(['close', 'deleted']);

const {
  createQuery,
  createQueryLoading,
  updateQuery,
  updateQueryLoading,
  deleteQuery,
  deleteQueryLoading: deleteLoading,
  getDatasetsInGroup,
  getDatasetsInGroupLoading,
} = useMSPowerBI();
const mutateLoading = computed(() => createQueryLoading.value || updateQueryLoading.value);

const syncLoading = ref(false);
const defaultMSPowerBIQuery = () => ({
  groupId: null,
  groupName: null,
  datasetId: null,
  datasetName: null,
  dax: '',
});

const localQuery = ref(defaultMSPowerBIQuery());
if (props.query !== null) {
  localQuery.value = copy(props.query);
}

const isDirty = ref(false);

const hasConnectionError = computed(() => [dataSourceQueryGenericError.incompleteSetup, dataSourceQueryGenericError.unauthenticated].includes(props.dataSource[customDatasourceProperty.dataSourceError]));
const hasDataSourceError = computed(() => !isNullOrUndefined(props.dataSource[customDatasourceProperty.dataSourceError]));
const hasQueryError = computed(() => !isDirty.value && !isNullOrUndefined(props.query?.error));
const hasError = computed(() => hasDataSourceError.value || hasQueryError.value);
const error = computed(() => {
  if (hasDataSourceError.value) {
    return props.dataSource[customDatasourceProperty.dataSourceError];
  }
  if (hasQueryError.value) {
    return props.query?.error;
  }
  return null;
});

const groups = computed(() => props.dataSource[customDatasourceProperty.msPowerBiGroups].map((group) => {
  if (group.id === 'self') {
    return { id: group.id, name: t('msPowerBIForm.selfGroup') };
  }
  return group;
}));

const selectedGroup = computed(() => {
  if (localQuery.value.groupId === null) {
    return null;
  }

  const group = props.dataSource[customDatasourceProperty.msPowerBiGroups].find((r) => r.id === localQuery.value.groupId);
  if (group === undefined) {
    return null;
  }

  return group;
});

const changeGroup = () => {
  localQuery.value = {
    ...localQuery.value,
    ...defaultMSPowerBIQuery(),
    groupId: localQuery.value.groupId,
  };
  isDirty.value = true;
};

watch(selectedGroup, (newValue, oldValue) => {
  if (isNullOrUndefined(newValue)) {
    return;
  }
  if (!isNullOrUndefined(oldValue) && newValue.id === oldValue.id) {
    return;
  }
  getDatasetsInGroup(props.dataSource, newValue).catch(() => {
    snackbar.error();
  });
}, { immediate: true });

const selectedDataset = computed(() => {
  if (localQuery.value.datasetId === null) {
    return null;
  }

  const dataset = props.dataSource[customDatasourceProperty.msPowerBiDatasets].find((r) => r.id === localQuery.value.datasetId);
  if (dataset === undefined) {
    return null;
  }

  return dataset;
});

const changeDataset = () => {
  isDirty.value = true;
};

const isDAXValid = computed(() => localQuery.value.dax.startsWith('EVALUATE'));
const isValid = computed(() => selectedGroup.value !== null && selectedDataset.value !== null && isDAXValid.value);

const save = () => {
  if (typeof localQuery.value.uid === 'undefined') {
    createMsPowerBIQuery(localQuery.value);
    return;
  }

  updateMsPowerBIQuery(localQuery.value);
};
const createMsPowerBIQuery = (query) => {
  createQuery({
    ...query,
    groupName: selectedGroup.value.name ?? '',
    datasetName: selectedDataset.value.name,
    dataSource: { uid: props.dataSource.uid },
    goal: { uid: props.goal.uid },
  }).then((query) => {
    localQuery.value = copy(query);
    isDirty.value = false;
  }).catch(logCatch(() => {
    snackbar.error();
  }));
};
const updateMsPowerBIQuery = (query) => {
  updateQuery({
    ...query,
    groupName: selectedGroup.value.name ?? '',
    datasetName: selectedDataset.value.name,
    dataSource: { uid: props.dataSource.uid },
  }).then((query) => {
    localQuery.value = copy(query);
    isDirty.value = false;
  }).catch(logCatch(() => {
    snackbar.error();
  }));
};
const syncNow = () => {
  syncLoading.value = true;
  updateQuery(props.goal.msPowerBiQuery).then((query) => {
    localQuery.value = copy(query);
    isDirty.value = false;
    snackbar.success(t('dataSource.successfullySynced'));
  }).catch(logCatch(() => {
    snackbar.error();
  })).finally(() => {
    syncLoading.value = false;
  });
};
const confirmDialog = useConfirmDialog();
const deleteMsPowerBIQuery = () => {
  const deleteMethod = () => deleteQuery(props.goal.msPowerBiQuery.uid).then(() => {
    emit('deleted');
    emit('close');
  }).catch(() => {
    snackbar.error();
  });

  confirmDialog.confirm({
    title: t('dataSource.deleteConnectionPrompt'),
    onOk() {
      deleteMethod();
    },
  });
};
</script>

<style scoped lang="scss" type="text/scss">
.ms-power-bi-form {
  ._item {
    margin-bottom: 1.4rem;

    ._label {
      margin-bottom: .4rem;
      color: $font-color-secondary;
    }
  }

  ._actions {
    display: flex;

    ._btns {
      display: flex;
      margin-left: auto;

      ._btn {
        margin-left: .4rem;
      }
    }
  }
}
</style>
